import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../../../shared/i18n';
import { childrenApi, pendingActivitiesApi } from '../../api/guardians';
import { fetchDashboard } from '../../api/dashboard';
import { WizardProgressBar } from './WizardProgressBar';
import { WizardSubscriptionStep } from './WizardSubscriptionStep';
import { WizardActivitySelectionStep } from './WizardActivitySelectionStep';
import { WizardActivityConfigStep } from './WizardActivityConfigStep';
import { WizardGoalStep } from './WizardGoalStep';
import { ChildSubscriptionModal } from '../ChildSubscriptionModal';

export const KidSetupWizard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { childId } = useParams();

  // Wizard phases: 'subscription' | 'select' | 'configure' | 'goal'
  const [wizardPhase, setWizardPhase] = useState('subscription');
  const [currentActivityIndex, setCurrentActivityIndex] = useState(0);
  const [dailyTarget, setDailyTarget] = useState(100);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  // Track original total and completed count for proper "X/Y" display
  const [originalActivityCount, setOriginalActivityCount] = useState(0);
  const [completedActivityCount, setCompletedActivityCount] = useState(0);

  const formatNumber = (num) => {
    return new Intl.NumberFormat('vi-VN').format(num);
  };

  // Fetch child data - staleTime: 0 ensures fresh data when returning from payment
  const { data: childData, isLoading: isLoadingChild, refetch: refetchChild } = useQuery({
    queryKey: ['child', childId],
    queryFn: () => childrenApi.get(childId),
    enabled: !!childId,
    staleTime: 0,
    refetchOnMount: 'always'
  });

  // Fetch dashboard data for family info
  const { data: dashboardData, isLoading: isLoadingDashboard } = useQuery({
    queryKey: ['dashboard'],
    queryFn: fetchDashboard
  });

  const child = childData?.data?.child;
  const family = dashboardData?.family;
  const pendingActivities = child?.pending_activity_template_keys || [];
  const activeActivities = child?.active_activity_template_keys || [];
  // Combined list for selection display (both pending and already configured)
  const allSelectedActivities = [...new Set([...pendingActivities, ...activeActivities])];

  // Current activity being configured
  const currentTemplateKey = pendingActivities[currentActivityIndex];

  // Auto-skip subscription step if child is already subscribed
  useEffect(() => {
    if (child?.subscribed && wizardPhase === 'subscription') {
      setWizardPhase('select');
    }
  }, [child?.subscribed, wizardPhase]);

  // Handle continue with free subscription
  const handleContinueWithFree = () => {
    setWizardPhase('select');
  };

  // Handle back from subscription (close wizard since it's the first step)
  const handleBackFromSubscription = () => {
    setShowExitConfirm(true);
  };

  // Handle activity selection complete
  const handleSelectionComplete = (selectedTemplates) => {
    if (selectedTemplates.length === 0) {
      // No activities selected, go to goal step
      setWizardPhase('goal');
    } else {
      // Start configuring activities - track the original count
      setOriginalActivityCount(selectedTemplates.length);
      setCompletedActivityCount(0);
      setCurrentActivityIndex(0);
      setWizardPhase('configure');
      // Refetch child to get updated pending_activity_template_keys
      refetchChild();
    }
  };

  // Handle activity configuration complete
  const handleActivityComplete = async (templateKey) => {
    // Remove from pending list on backend
    try {
      await pendingActivitiesApi.remove(childId, templateKey);

      // Refetch child data
      await refetchChild();

      // Invalidate queries to refresh data
      queryClient.invalidateQueries(['child', childId]);
      queryClient.invalidateQueries(['dashboard']);

      // Check updated pending list
      const updatedChild = await childrenApi.get(childId);
      const updatedPending = updatedChild?.data?.child?.pending_activity_template_keys || [];

      // Increment completed count and move to next activity or goal step
      setCompletedActivityCount(prev => prev + 1);
      if (updatedPending.length === 0) {
        setWizardPhase('goal');
      } else {
        setCurrentActivityIndex(0); // Reset to first remaining activity in the list
      }
    } catch (error) {
      console.error('Failed to update pending activities:', error);
    }
  };

  // Handle activity skip
  const handleActivitySkip = async (templateKey) => {
    // Remove from pending list on backend
    try {
      await pendingActivitiesApi.remove(childId, templateKey);

      // Refetch child data
      await refetchChild();

      // Check updated pending list
      const updatedChild = await childrenApi.get(childId);
      const updatedPending = updatedChild?.data?.child?.pending_activity_template_keys || [];

      // Increment completed count (skipped counts as processed) and move to next
      setCompletedActivityCount(prev => prev + 1);
      if (updatedPending.length === 0) {
        setWizardPhase('goal');
      } else {
        setCurrentActivityIndex(0); // Reset to first remaining activity in the list
      }
    } catch (error) {
      console.error('Failed to update pending activities:', error);
    }
  };

  // Handle back from configure phase
  const handleConfigBack = () => {
    // Reset counters when going back to selection
    setOriginalActivityCount(0);
    setCompletedActivityCount(0);
    setWizardPhase('select');
  };

  // Handle back from selection phase (go back to subscription)
  const handleBackFromSelection = () => {
    setWizardPhase('subscription');
  };

  // Handle skip activity selection
  const handleSkipSelection = () => {
    setWizardPhase('goal');
  };

  // Handle goal step complete - finish wizard
  const handleGoalComplete = (targetXp) => {
    sessionStorage.setItem('flash_notice', `Đã thiết lập ${child?.name || child?.username} thành công!`);
    navigate('/');
  };

  // Handle skip goal step - finish wizard
  const handleSkipGoal = () => {
    sessionStorage.setItem('flash_notice', `Đã thiết lập ${child?.name || child?.username} thành công!`);
    navigate('/');
  };

  // Handle back from goal step
  const handleBackFromGoal = () => {
    if (pendingActivities.length > 0) {
      setWizardPhase('configure');
    } else {
      setWizardPhase('select');
    }
  };

  // Handle close wizard
  const handleCloseWizard = () => {
    setShowExitConfirm(true);
  };

  const handleConfirmExit = () => {
    sessionStorage.setItem('flash_notice', `Đã thêm ${child?.name} vào gia đình.`);
    navigate('/');
  };

  const handleCancelExit = () => {
    setShowExitConfirm(false);
  };

  // Calculate current step for progress bar
  const getCurrentStep = () => {
    switch (wizardPhase) {
      case 'subscription': return 1;
      case 'select': return 2;
      case 'configure': return 2; // Still in step 2 (activities)
      case 'goal': return 3;
      default: return 1;
    }
  };

  if (isLoadingChild || isLoadingDashboard) {
    return (
      <div className="container py-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!child) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger">
          Không tìm thấy thông tin trẻ
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-12 col-lg-8">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3 className="mb-0">Thiết Lập {child.username}</h3>
            <button
              type="button"
              className="btn btn-link text-muted"
              onClick={handleCloseWizard}
              data-testid="wizard-close-btn"
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>

          {/* Progress bar */}
          <WizardProgressBar
            currentStep={getCurrentStep()}
            wizardPhase={wizardPhase}
            configProgress={originalActivityCount > 0 ? {
              current: completedActivityCount + 1,
              total: originalActivityCount
            } : null}
          />

          {/* Step content */}
          <div className="card">
            <div className="card-body p-4">
              {/* Phase: Subscription */}
              {wizardPhase === 'subscription' && (
                <WizardSubscriptionStep
                  child={child}
                  family={family}
                  onContinueWithFree={handleContinueWithFree}
                  onContinue={handleContinueWithFree}
                  onBack={handleBackFromSubscription}
                  activityCounts={family ? {
                    freeActivitiesCount: family.free_activities_count,
                    paidActivitiesCount: family.paid_activities_count,
                  } : null}
                />
              )}

              {/* Phase: Activity Selection */}
              {wizardPhase === 'select' && (
                <WizardActivitySelectionStep
                  childId={childId}
                  childName={child.username}
                  initialSelectedTemplates={allSelectedActivities}
                  alreadyConfiguredTemplates={activeActivities}
                  onContinue={handleSelectionComplete}
                  onSkip={handleSkipSelection}
                  onBack={handleBackFromSelection}
                />
              )}

              {/* Phase: Activity Configuration */}
              {wizardPhase === 'configure' && currentTemplateKey && (
                <WizardActivityConfigStep
                  key={currentTemplateKey}
                  childId={childId}
                  childUsername={child.username}
                  templateKey={currentTemplateKey}
                  currentIndex={completedActivityCount}
                  totalCount={originalActivityCount}
                  onComplete={handleActivityComplete}
                  onSkip={handleActivitySkip}
                  onBack={handleConfigBack}
                />
              )}

              {/* Phase: Goal Setting */}
              {wizardPhase === 'goal' && (
                <WizardGoalStep
                  childId={childId}
                  childName={child.username}
                  initialValue={dailyTarget}
                  hasSubscription={child.subscribed}
                  maxFreeXp={family?.max_free_xp}
                  onContinue={handleGoalComplete}
                  onSkip={handleSkipGoal}
                  onBack={handleBackFromGoal}
                  onUpgrade={() => setShowSubscriptionModal(true)}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Exit confirmation modal */}
      {showExitConfirm && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Thoát Thiết Lập</h5>
                <button type="button" className="btn-close" onClick={handleCancelExit}></button>
              </div>
              <div className="modal-body">
                <p>Bạn có chắc muốn thoát?</p>
                <p className="text-muted small">
                  Bạn có thể tiếp tục thiết lập sau từ trang Dashboard.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancelExit}
                >
                  Tiếp Tục Thiết Lập
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleConfirmExit}
                >
                  Thoát
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription modal */}
      {showSubscriptionModal && family && child && (
        <ChildSubscriptionModal
          child={child}
          family={family}
          mode="subscribe"
          onClose={() => setShowSubscriptionModal(false)}
          t={t}
          formatNumber={formatNumber}
          activityCounts={{
            freeActivitiesCount: family.free_activities_count,
            paidActivitiesCount: family.paid_activities_count,
          }}
        />
      )}
    </div>
  );
};
